import { getOpeningEloProfile } from "../data/C1_OpeningEloDefinitions";
import { getEloItemProfile, getRoomEloItemProfile } from "../data/C1_eloItemsRegistry";
import type {
  C1_ButtonDefaultsByType,
  C1_EloItem_Definition,
  C1_EloItem_Details,
  C1_EloItem_Data,
} from "../types/C1_EloTypes";
import { C1_EloItem_Type, createDefaultButtonProperties } from "../types/C1_EloTypes";
import type { C1_EquipmentStandard } from "../types/C1_ModelTypes";
import type { C1_Project } from "./C1_Project";
import { newId } from "../../../src/models/BaseObject";
import { getButtonProductDefinition } from "../data/C1_ProductDefinitions";

const detailDefault = <T>(
  option: { default?: Partial<Record<`S${C1_EquipmentStandard}`, T>> } | undefined,
  equipmentStandard: C1_EquipmentStandard,
) => option?.default?.[`S${equipmentStandard}`];

export function isEloItemDefaultEnabled(
  definition: C1_EloItem_Definition,
  equipmentStandard: C1_EquipmentStandard,
): boolean {
  if (definition.allways) return true;
  const value = definition.defaultEnabled;
  if (value === undefined) return true;
  if (typeof value === "boolean") return value;
  return value?.[`S${equipmentStandard}`] === true;
}

function initialDetails(
  definition: C1_EloItem_Definition,
  equipmentStandard: C1_EquipmentStandard,
): C1_EloItem_Details | undefined {
  const options = definition.details;
  if (!options) return definition.defaultDetails ? { ...definition.defaultDetails } : undefined;

  return {
    itemCount: options.defaultItemCount ?? (Array.isArray(options.repeatCount) ? undefined : options.repeatCount),
    numberOfButtonChannels: options.numberOfButtonChannels,
    buttonType: detailDefault(options.buttonType, equipmentStandard),
    channelType: detailDefault(options.channelType, equipmentStandard),
    lightProductId: detailDefault(options.lightProductId, equipmentStandard),
    plugType: detailDefault(options.plugType, equipmentStandard),
    voltage: detailDefault(options.voltage, equipmentStandard),
    sensorTypes: detailDefault(options.sensorTypes, equipmentStandard),
    ...definition.defaultDetails,
  };
}

function expandedDefinitions(definition: C1_EloItem_Definition, details?: C1_EloItem_Details): C1_EloItem_Definition[] {
  const items = definition.items ?? [];
  if (definition.type !== C1_EloItem_Type.Group && definition.type !== C1_EloItem_Type.PlugGroup) return items;
  if (definition.details?.repeatCount === undefined) return items;

  const itemCount =
    details?.itemCount ??
    definition.details.defaultItemCount ??
    (Array.isArray(definition.details.repeatCount) ? undefined : definition.details.repeatCount) ??
    1;
  return Array.from({ length: Math.max(0, Math.floor(itemCount)) }, (_, index) => index + 1).flatMap((repeatIndex) =>
    items.map((item) => ({
      ...item,
      id: `${item.id}-${repeatIndex}`,
      repeatIndex,
      defaultDetails: { ...item.defaultDetails, ...definition.repeatDefaults?.[repeatIndex - 1]?.details },
      description: {
        en: `${item.description.en} #${repeatIndex}`,
        de: `${item.description.de} #${repeatIndex}`,
      },
    })),
  );
}

export function createEloItemInstance(
  definition: C1_EloItem_Definition,
  equipmentStandard: C1_EquipmentStandard,
  buttonDefaults?: C1_ButtonDefaultsByType,
  wallDocked?: boolean,
): C1_EloItem_Data {
  const details = initialDetails(definition, equipmentStandard);
  const buttonProduct = getButtonProductDefinition(details?.buttonType);
  const buttonPreferences =
    buttonProduct?.uiTemplate === "simple" || buttonProduct?.uiTemplate === "touch"
      ? buttonDefaults?.[buttonProduct.uiTemplate]
      : undefined;
  return {
    id: newId(),
    definitionId: definition.id,
    name: definition.defaultName ?? definition.description.de,
    details,
    properties:
      definition.type === C1_EloItem_Type.Button
        ? createDefaultButtonProperties(buttonProduct, buttonPreferences)
        : undefined,
    items: expandedDefinitions(definition, details)
      .filter((item) => !item.onlyIfWallDocked || wallDocked !== false)
      .filter((item) => isEloItemDefaultEnabled(item, equipmentStandard))
      .map((item) => createEloItemInstance(item, equipmentStandard, buttonDefaults, wallDocked)),
  };
}

export function createEquipmentInstance(
  definitionId: string,
  equipmentStandard: C1_EquipmentStandard,
  definitions: C1_EloItem_Definition[] = [],
  buttonDefaults?: C1_ButtonDefaultsByType,
  wallDocked = false,
): C1_EloItem_Data {
  return {
    id: newId(),
    definitionId,
    items: definitions
      .filter((definition) => !definition.onlyIfWallDocked || wallDocked)
      .filter((definition) => isEloItemDefaultEnabled(definition, equipmentStandard))
      .map((definition) => createEloItemInstance(definition, equipmentStandard, buttonDefaults, wallDocked)),
  };
}

/** Adds docking-dependent defaults without restoring unrelated equipment disabled by the user. */
export function addWallDockedEloDefaults(
  tree: C1_EloItem_Data,
  definitions: C1_EloItem_Definition[],
  equipmentStandard: C1_EquipmentStandard,
  buttonDefaults?: C1_ButtonDefaultsByType,
): C1_EloItem_Data {
  let changed = false;
  const items = tree.items.map((item) => {
    const definition = definitions.find((candidate) => candidate.id === item.definitionId);
    if (!definition) return item;
    const next = addWallDockedEloDefaults(item, expandedDefinitions(definition, item.details), equipmentStandard, buttonDefaults);
    if (next !== item) changed = true;
    return next;
  });
  for (const definition of definitions) {
    if (!definition.onlyIfWallDocked || !isEloItemDefaultEnabled(definition, equipmentStandard)) continue;
    if (items.some((item) => item.definitionId === definition.id)) continue;
    items.push(createEloItemInstance(definition, equipmentStandard, buttonDefaults, true));
    changed = true;
  }
  return changed ? { ...tree, items } : tree;
}

export function reconcileEloEquipmentInstance(
  definitionId: string,
  definitions: C1_EloItem_Definition[],
  equipmentStandard: C1_EquipmentStandard,
  existing?: C1_EloItem_Data,
  buttonDefaults?: C1_ButtonDefaultsByType,
): C1_EloItem_Data {
  if (definitionId === "door-passage" && existing?.definitionId === "door-internal") {
    const passageAliases: Record<string, string> = {
      "door-internal.button-inside": "passage.button-inside-left",
      "door-internal.plug-inside": "passage.plug-inside-left",
      "door-internal.button-outside": "passage.button-outside-left",
    };
    existing = { ...existing, items: existing.items.map(item => ({
      ...item, definitionId: passageAliases[item.definitionId] ?? item.definitionId,
    })) };
  }
  const reconcileItem = (definition: C1_EloItem_Definition, current: C1_EloItem_Data): C1_EloItem_Data => {
    const childDefinitions = expandedDefinitions(definition, current.details);
    return {
      ...current,
      definitionId: definition.id,
      name: current.name?.trim() || (definition.defaultName ?? definition.description.de),
      properties:
        definition.type === C1_EloItem_Type.Button
          ? current.properties?.type === C1_EloItem_Type.Button
            ? current.properties
            : createDefaultButtonProperties(
                getButtonProductDefinition(current.details?.buttonType),
                (() => {
                  const template = getButtonProductDefinition(current.details?.buttonType)?.uiTemplate;
                  return template === "simple" || template === "touch" ? buttonDefaults?.[template] : undefined;
                })(),
              )
          : current.properties,
      items: childDefinitions.flatMap((childDefinition) => {
        const currentChild = current.items.find((item) => item.definitionId === childDefinition.id);
        if (currentChild) return [reconcileItem(childDefinition, currentChild)];
        return isEloItemDefaultEnabled(childDefinition, equipmentStandard)
          ? [createEloItemInstance(childDefinition, equipmentStandard, buttonDefaults)]
          : [];
      }),
    };
  };

  return {
    id: existing?.id ?? newId(),
    ...(existing?.tag !== undefined ? { tag: existing.tag } : {}),
    definitionId,
    items: definitions.flatMap((definition) => {
      const current = existing?.items.find((item) => item.definitionId === definition.id);
      if (current) return [reconcileItem(definition, current)];
      return isEloItemDefaultEnabled(definition, equipmentStandard)
        ? [createEloItemInstance(definition, equipmentStandard, buttonDefaults)]
        : [];
    }),
  };
}

export function applyOpeningContactDefault(
  opening: import("./C1_Opening").C1_Opening,
  project: C1_Project,
  enabled: boolean,
): void {
  const profile = getOpeningEloProfile(opening);
  const reconciled = reconcileEloEquipmentInstance(
    profile.rootDefinitionId,
    profile.definitions,
    project.equipmentStandard,
    opening.eloTree,
    project.settings.buttonDefaults,
  );
  const contactDefinitions = profile.definitions.filter(
    (definition) => definition.type === C1_EloItem_Type.WindowContact,
  );
  const contactIds = new Set(contactDefinitions.map((definition) => definition.id));
  const itemsWithoutContacts = reconciled.items.filter((item) => !contactIds.has(item.definitionId));
  const contactItems = enabled
    ? contactDefinitions.map((definition) => {
        const existing = reconciled.items.find((item) => item.definitionId === definition.id);
        return (
          existing ?? createEloItemInstance(definition, project.equipmentStandard, project.settings.buttonDefaults)
        );
      })
    : [];
  opening.setEloTree({
    ...reconciled,
    items: [...itemsWithoutContacts, ...contactItems],
  });
}

/** Creates missing electrical defaults throughout the project and preserves every existing instance. */
export function ensureEloEquipmentDefaults(project: C1_Project): void {
  project.floors.forEach((floor) => {
    floor.rooms.forEach((room) => {
      room.setEloTree(
        reconcileEloEquipmentInstance(
          "room",
          getRoomEloItemProfile(room.type),
          project.equipmentStandard,
          room.eloTree,
          project.settings.buttonDefaults,
        ),
      );

      room.furnitures.forEach((furniture) => {
        furniture.syncWallDockedEloItems(true);
        if (furniture.eloTree?.items.length) return;
        const definitions = getEloItemProfile(furniture.definitionId);
        furniture.setEloTree(
          createEquipmentInstance(
            furniture.definitionId,
            project.equipmentStandard,
            definitions,
            project.settings.buttonDefaults,
            furniture.wallDocked !== undefined,
          ),
        );
      });
    });

    floor.rooms.flatMap((room) => room.openings).forEach((opening) => {
      const profile = getOpeningEloProfile(opening);
      opening.setEloTree(
        reconcileEloEquipmentInstance(
          profile.rootDefinitionId,
          profile.definitions,
          project.equipmentStandard,
          opening.eloTree,
          project.settings.buttonDefaults,
        ),
      );
    });
  });
}
